// src/utils/meetings.js
// ============================================================
// SHARED TODAY FILTER & LIFECYCLE LOGIC FOR MEETINGS
// ============================================================

/** Returns today's date as "YYYY-MM-DD" in local time. */
export function getTodayStr() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Calculate meeting end time (HH:mm) given start time and duration (mins) */
export function calculateEndTime(startTime, durationMins) {
  if (!startTime) return "";
  if (!durationMins) return startTime;
  const [h, m] = startTime.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  d.setMinutes(d.getMinutes() + parseInt(durationMins, 10));
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Add 30 minutes to a HH:mm time string and return HH:mm */
export function add30Minutes(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  d.setMinutes(d.getMinutes() + 30);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Calculate meeting duration in minutes from start and end time HH:mm */
export function getDurationMins(startStr, endStr) {
  if (!startStr || !endStr) return 30;
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = endStr.split(":").map(Number);
  const sDate = new Date();
  sDate.setHours(sh, sm, 0, 0);
  const eDate = new Date();
  eDate.setHours(eh, em, 0, 0);
  let diff = (eDate.getTime() - sDate.getTime()) / 60000;
  if (diff < 0) {
    diff += 24 * 60; // handle wrap around midnight
  }
  return diff;
}

/** Formats HH:mm time string to a user-friendly format: e.g. "10:00 AM" */
export function formatTimeStr(timeStr) {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  let [h, m] = parts.map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  h = h ? h : 12; // the hour '0' should be '12'
  const minStr = String(m).padStart(2, "0");
  return `${h}:${minStr} ${ampm}`;
}

/** Helper to check if a user has joined the meeting */
export function isUserJoined(meeting, userProfile) {
  const userId = userProfile?.uid || userProfile?.id;
  if (!userId) return false;
  
  if (meeting.attendees && Array.isArray(meeting.attendees) && meeting.attendees.includes(userId)) {
    return true;
  }
  if (userProfile?.currentMeetingId === meeting.id) {
    return true;
  }
  return false;
}

/** Determine the automatic meeting lifecycle state */
export function getMeetingState(meeting, userProfile) {
  const today = getTodayStr();
  
  if (meeting.date < today) {
    return isUserJoined(meeting, userProfile) ? "COMPLETED" : "MISSED";
  }
  if (meeting.date > today) {
    return "UPCOMING";
  }

  const now = new Date();
  const startTimeStr = meeting.startTime || meeting.time;
  const endTimeStr = meeting.endTime || (meeting.time ? calculateEndTime(meeting.time, meeting.duration || 60) : "");

  if (!startTimeStr) return "UPCOMING";

  const start = new Date(`${meeting.date}T${startTimeStr}`);
  const end = endTimeStr ? new Date(`${meeting.date}T${endTimeStr}`) : new Date(start.getTime() + 60 * 60 * 1000);

  if (now > end) {
    return isUserJoined(meeting, userProfile) ? "COMPLETED" : "MISSED";
  }
  if (now >= start && now <= end) {
    return "LIVE";
  }
  return "UPCOMING";
}

/** Determine if a meeting is visible to a given employee based on role and audience */
export function isEmployeeVisible(meeting, userProfile) {
  if (!userProfile) return false;
  
  const role = (userProfile.role || "").toLowerCase();
  if (role === "admin") return true;

  const userId = userProfile.uid || userProfile.id;
  const empId = userProfile.employeeId;
  const dept = userProfile.department;
  const teamId = userProfile.teamId || userProfile.teamLeadId;

  // 1. If user scheduled the meeting
  if (meeting.scheduledBy === userId) return true;

  // 2. employeeId or userId exists in participants array
  if (meeting.participants && Array.isArray(meeting.participants)) {
    if (userId && meeting.participants.includes(userId)) return true;
    if (empId && meeting.participants.includes(empId)) return true;
  }

  // 3. audience === ALL
  const audience = (meeting.audience || "").toLowerCase();
  if (audience === "all") return true;

  // 4. Team lead check — only leads/team_leads see "leads" audience
  if (audience === "leads" && (role === "lead" || role === "team_lead")) {
    return true;
  }

  // 5. Employees only — only employees (not leads) see "employees" audience
  if (audience === "employees" && role === "employee") {
    return true;
  }

  // 6. employee belongs to assigned department/team
  if (dept && audience === dept.toLowerCase()) return true;
  if (teamId && (meeting.audience || "").toLowerCase() === teamId.toLowerCase()) return true;

  return false;
}

/**
 * Centralized getTodaysMeetings filter.
 * STRICT RULE: Only show meetings where meeting.date === today.
 * Do NOT show: yesterday meetings, tomorrow meetings, future meetings, old completed meetings.
 */
export function getTodaysMeetings(meetings, userProfile) {
  const today = getTodayStr();
  return (meetings || [])
    .filter((m) => {
      // 1. Date must be exactly today
      if (m.date !== today) return false;

      // 2. Participant/audience visibility check
      if (!isEmployeeVisible(m, userProfile)) return false;

      // 3. Exclude completed/missed meetings (where endTime has passed)
      const state = getMeetingState(m, userProfile);
      if (state === "COMPLETED" || state === "MISSED") return false;

      return true;
    })
    .sort((a, b) => {
      const t1 = a.startTime || a.time || "";
      const t2 = b.startTime || b.time || "";
      return t1.localeCompare(t2);
    });
}
